import { useEffect, useId, useState } from "react";
import { Pencil, ListTree } from "lucide-react";
import { api } from "../lib/api";
import { LocationPin } from "./LocationPin";
import { SmartSelect } from "./SmartInputs";

/**
 * v3.9.35 — cascading Philippine location picker (CEO #5). Region → Province or
 * NCR District → City/Municipality → Barangay, each level lazy-loaded from /api/geo, plus optional
 * Address + Zip. Emits a composed display string (what listings store today) via
 * onChange, so it's a drop-in for the old free-text location field. A "type it
 * manually" escape hatch keeps rare/unlisted places usable.
 */
type Opt = { code: string; name: string };
const NCR_REGION_CODE = "13";
/** Capitalizes PSGC names without treating accented letters as word breaks. */
const titleCase = (value: string) =>
  value
    .toLocaleLowerCase("en-PH")
    .replace(/(^|[\s-])\p{L}/gu, (match) => match.toLocaleUpperCase("en-PH"))
    .replace(/\b(?:Of|And)\b/gu, (word) => word.toLocaleLowerCase("en-PH"));

export const emptyLocationDraft = () => ({ manual: false, selection: { region: "", regionName: "", province: "", provinceName: "", city: "", cityName: "", barangay: "", address: "", zip: "" } });
export type LocationPickerDraft = ReturnType<typeof emptyLocationDraft>;

export function LocationPicker({
  value, onChange, cityOnly = false, searchable = true, draft, onDraftChange, invalid, describedBy,
}: {
  draft?: LocationPickerDraft;
  onDraftChange?: (value: LocationPickerDraft) => void;
  value: string;
  onChange: (display: string, parts?: { region?: string; province?: string; city?: string; barangay?: string; address?: string; zip?: string }) => void;
  cityOnly?: boolean;
  searchable?: boolean;
  invalid?: boolean;
  describedBy?: string;
}) {
  const fieldId = useId();
  const [localDraft, setLocalDraft] = useState(emptyLocationDraft);
  const state = draft ?? localDraft;
  const update = onDraftChange ?? setLocalDraft;
  const manual = state.manual;
  const sel = state.selection;
  const setManual = (manual: boolean) => update({ ...state, manual });
  const setSel = (selection: typeof sel) => update({ ...state, selection });
  const [regions, setRegions] = useState<Opt[]>([]);
  const [provinces, setProvinces] = useState<Opt[]>([]);
  const [cities, setCities] = useState<Opt[]>([]);
  const [barangays, setBarangays] = useState<Opt[]>([]);

  useEffect(() => { api.geoRegions().then((r) => setRegions(r.items)).catch(() => setRegions([])); }, []);

  useEffect(() => {
    let active = true;
    setProvinces([]);
    if (sel.region) api.geoProvinces(sel.region).then(r => { if (active) setProvinces(r.items); }).catch(() => { if (active) setProvinces([]); });
    return () => { active = false; };
  }, [sel.region]);
  useEffect(() => {
    let active = true;
    setCities([]);
    if (sel.province) api.geoCities(sel.province).then(r => { if (active) setCities(r.items); }).catch(() => { if (active) setCities([]); });
    return () => { active = false; };
  }, [sel.province]);
  useEffect(() => {
    let active = true;
    setBarangays([]);
    if (sel.city) api.geoBarangays(sel.city).then(r => { if (active) setBarangays(r.items); }).catch(() => { if (active) setBarangays([]); });
    return () => { active = false; };
  }, [sel.city]);

  function emit(next: typeof sel) {
    const parts = [cityOnly ? "" : next.address, cityOnly ? "" : next.barangay, next.cityName ? titleCase(next.cityName) : "", next.provinceName ? titleCase(next.provinceName) : ""]
      .map((s) => (s || "").trim()).filter(Boolean);
    let display = parts.join(", ");
    if (next.zip && !cityOnly) display += ` ${next.zip}`;
    onChange(cityOnly && !next.cityName ? "" : display.trim(), {
      region: next.regionName, province: next.provinceName, city: next.cityName, barangay: next.barangay, address: next.address, zip: next.zip,
    });
  }

  function pickRegion(code: string) {
    if (code === sel.region) return;
    const regionName = regions.find((r) => r.code === code)?.name || "";
    const next = { ...sel, region: code, regionName, province: "", provinceName: "", city: "", cityName: "", barangay: "" };
    setSel(next); setProvinces([]); setCities([]); setBarangays([]); emit(next);
  }
  function pickProvince(code: string) {
    if (code === sel.province) return;
    const provinceName = provinces.find((p) => p.code === code)?.name || "";
    const next = { ...sel, province: code, provinceName, city: "", cityName: "", barangay: "" };
    setSel(next); setCities([]); setBarangays([]); emit(next);
  }
  function pickCity(code: string) {
    if (code === sel.city) return;
    const cityName = cities.find((c) => c.code === code)?.name || "";
    const next = { ...sel, city: code, cityName, barangay: "" };
    setSel(next); setBarangays([]); emit(next);
  }
  function pickBarangay(name: string) { const next = { ...sel, barangay: name }; setSel(next); emit(next); }
  function setField(k: "address" | "zip", v: string) { const next = { ...sel, [k]: v }; setSel(next); emit(next); }

  if (manual && !cityOnly) {
    return (
      <div className="enhanced-form-fields">
        <input className={`field ${invalid ? "border-brand-red" : ""}`} aria-label="Location" aria-invalid={invalid || undefined} aria-describedby={describedBy} value={value} onChange={(e) => onChange(e.target.value)} placeholder="City / Province" />
        <button type="button" onClick={() => setManual(false)} className="text-xs text-brand-red mt-1.5 inline-flex items-center gap-1"><ListTree className="w-3 h-3" />Pick from list instead</button>
      </div>
    );
  }

  const selectCls = "field";
  const isNcr = sel.region === NCR_REGION_CODE;
  // [posting.location-defaults](../../../docs/product/marketplace.md#posting-location-defaults): typing filters names only; a list selection resolves the geography
  // code before advancing the cascade. Custom places use the manual fallback.
  const searchField = (label: string, options: Opt[], code: string, onSelect: (code: string) => void, disabled = false) => {
    const id = `${fieldId}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
    return <div>
      <label htmlFor={id} className="label">{label}</label>
      <SmartSelect id={id} invalid={invalid} describedBy={describedBy} value={titleCase(options.find((option) => option.code === code)?.name || "")} options={options.map((option) => titleCase(option.name))} allowOther={false} disabled={disabled} placeholder={`Search ${label.toLowerCase()}…`} onChange={(name) => {
        const option = options.find((entry) => titleCase(entry.name) === name);
        if (option) onSelect(option.code);
      }} />
    </div>;
  };
  return (
    <div className="enhanced-form-fields space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {searchable ? <>
          {searchField("Region", regions, sel.region, pickRegion)}
          {searchField(isNcr ? "District" : "Province", provinces, sel.province, pickProvince, !sel.region)}
          {searchField("City / Municipality", cities, sel.city, pickCity, !sel.province)}
          {!cityOnly && searchField("Barangay", barangays, barangays.find((option) => option.name === sel.barangay)?.code || "", (code) => pickBarangay(barangays.find((option) => option.code === code)?.name || ""), !sel.city)}
        </> : <>
        <select className={selectCls} value={sel.region} onChange={(e) => pickRegion(e.target.value)}>
          <option value="">Region…</option>
          {regions.map((r) => <option key={r.code} value={r.code}>{titleCase(r.name)}</option>)}
        </select>
        <select className={selectCls} value={sel.province} onChange={(e) => pickProvince(e.target.value)} disabled={!sel.region}>
          <option value="">{sel.region ? (isNcr ? "District…" : "Province…") : "Province"}</option>
          {provinces.map((p) => <option key={p.code} value={p.code}>{titleCase(p.name)}</option>)}
        </select>
        <select className={selectCls} value={sel.city} onChange={(e) => pickCity(e.target.value)} disabled={!sel.province}>
          <option value="">{sel.province ? "City / Municipality…" : "City / Municipality"}</option>
          {cities.map((c) => <option key={c.code} value={c.code}>{titleCase(c.name)}</option>)}
        </select>
        {!cityOnly && <select className={selectCls} value={sel.barangay} onChange={(e) => pickBarangay(e.target.value)} disabled={!sel.city}>
          <option value="">{sel.city ? "Barangay…" : "Barangay"}</option>
          {barangays.map((b) => <option key={b.code} value={b.name}>{b.name}</option>)}
        </select>}
        </>}
      </div>
      {!cityOnly && <div className="grid grid-cols-[1fr_120px] gap-2">
        <div><label className="label" htmlFor={`${fieldId}-address`}>Street / building <span className="font-normal">(optional)</span></label><input id={`${fieldId}-address`} className="field" value={sel.address} onChange={(e) => setField("address", e.target.value)} /></div>
        <div><label className="label" htmlFor={`${fieldId}-zip`}>ZIP <span className="font-normal">(optional)</span></label><input id={`${fieldId}-zip`} className="field" value={sel.zip} onChange={(e) => setField("zip", e.target.value)} inputMode="numeric" /></div>
      </div>}
      {value && <p className="text-xs text-mut flex items-center gap-1"><LocationPin className="w-3 h-3" />{value}</p>}
      {!cityOnly && <button type="button" onClick={() => setManual(true)} className="text-xs text-brand-red inline-flex items-center gap-1"><Pencil className="w-3 h-3" />Can't find it? Type manually</button>}
    </div>
  );
}
